import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  NotificationCatalog, NotificationParameters
} from '../../../models/notifications/notification-catalog-entry.model';
import { NotificationDelivery } from '../../../models/notifications/notification-delivery.model';
import { NotificationEventSettings } from '../../../models/notifications/notification-event-settings.model';
import {
  NotificationCatalogMapper, NotificationDeliverySerializer, NotificationEventSettingsMapper,
  NotificationParametersMapper, WireCatalog, WireDelivery, WireEventSettings, WireParameters
} from '../../../models/notifications/notification-serializers.class';
import { QueryOptions } from '../../../models/query-options/query-options.class';
import { BaseApiService } from '../base/base-api.service';
import { CustomErrorHandlerService } from '../custom-error-handler.service';

export interface PagedFeed {
  currentpage: string;
  list: NotificationDelivery[];
  pages: string;
  pagesize: string;
  total: string;
}

function rethrow(error: HttpErrorResponse, errorHandler: CustomErrorHandlerService): Observable<never> {
  return throwError((): HttpErrorResponse => errorHandler.tryParseError(error));
}

/** Notification Center endpoints (spec §20). Always reached through APIService.notifications. */
@Injectable()
export class NotificationsService extends BaseApiService<NotificationDelivery> {
  private readonly catalog: NotificationCatalogMapper = new NotificationCatalogMapper();
  private readonly deliveries: NotificationDeliverySerializer = new NotificationDeliverySerializer();
  private readonly parameters: NotificationParametersMapper = new NotificationParametersMapper();
  private readonly settings: NotificationEventSettingsMapper = new NotificationEventSettingsMapper();

  public constructor(httpClient: HttpClient) {
    super(
      httpClient, undefined as unknown as string, 'notifications', new NotificationDeliverySerializer(),
      new CustomErrorHandlerService()
    );
  }

  public getCatalog(): Observable<NotificationCatalog> {
    return this.httpClient.get<object>(`${this.url}/notifications/catalog`).pipe(
      map((body: object): NotificationCatalog => this.catalog.fromJson(body as WireCatalog)),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  /** The option sets the schedule popover and condition row render (spec §7.3). */
  public getParameters(): Observable<NotificationParameters> {
    return this.httpClient.get<object>(`${this.url}/notifications/parameters`).pipe(
      map((body: object): NotificationParameters => this.parameters.fromJson(body as WireParameters)),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  /** One tenant-scoped policy row per visible event (GET /v1/notifications/settings). */
  public getSettings(): Observable<NotificationEventSettings[]> {
    return this.httpClient.get<object[]>(`${this.url}/notifications/settings`).pipe(
      map((rows: object[]): NotificationEventSettings[] => rows.map(
        (r: object): NotificationEventSettings => this.settings.fromJson(r as WireEventSettings)
      )),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  /**
   * Partial update: an event present in `rows` is replaced entirely, an event absent is left
   * untouched, and switching an event off is an explicit `channels: []`.
   */
  public saveSettings(rows: NotificationEventSettings[]): Observable<void> {
    const body: object[] = rows.map((r: NotificationEventSettings): object => this.settings.toJson(r));
    return this.httpClient.put<void>(`${this.url}/notifications/settings`, body).pipe(
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  public getFeed(qo: QueryOptions, unreadOnly: boolean): Observable<PagedFeed> {
    let params: HttpParams = new HttpParams()
      .set('Page', String(qo.page ?? 1))
      .set('PageSize', String(qo.pageSize ?? 20));
    if (qo.sorts) {
      params = params.set('Sorts', qo.sorts);
    }
    if (qo.filter) {
      params = params.set('filters', qo.filter);
    }
    if (unreadOnly) {
      params = params.set('unread', 'true');
    }
    return this.httpClient.get<object[]>(`${this.url}/notifications/feed`, { observe: 'response', params }).pipe(
      map((resp: HttpResponse<object[]>): PagedFeed => ({
        currentpage: resp.headers.get('currentpage') ?? '1',
        list: (resp.body ?? []).map((r: object): NotificationDelivery => this.deliveries.fromJson(r as WireDelivery)),
        pages: resp.headers.get('pages') ?? '1',
        pagesize: resp.headers.get('pagesize') ?? String(qo.pageSize ?? 20),
        total: resp.headers.get('total') ?? '0'
      })),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  /** One of the caller's own deliveries — used when a bell deep link points past the first feed page. */
  public getOne(id: string): Observable<NotificationDelivery> {
    return this.httpClient.get<object>(`${this.url}/notifications/${encodeURIComponent(id)}`).pipe(
      map((r: object): NotificationDelivery => this.deliveries.fromJson(r as WireDelivery)),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  public getUnreadCount(): Observable<number> {
    return this.httpClient.get<{ count: number }>(`${this.url}/notifications/unread-count`).pipe(
      map((r: { count: number }): number => r.count ?? 0),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  public markRead(id: string): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/notifications/${encodeURIComponent(id)}/read`, {}).pipe(
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  public markAllRead(): Observable<number> {
    return this.httpClient.post<{ marked: number }>(`${this.url}/notifications/read-all`, {}).pipe(
      map((r: { marked: number }): number => r.marked ?? 0),
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }

  /**
   * Re-queues the export a failed delivery describes (spec §13.6). 409 = the row cannot be retried,
   * 429 = one is already in flight; both surface through tryParseError so the component can show the
   * server's own reason rather than a generic failure.
   */
  public retryExport(id: string): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/notifications/${encodeURIComponent(id)}/retry`, {}).pipe(
      catchError((error: HttpErrorResponse): Observable<never> => rethrow(error, this.errorHandler))
    );
  }
}
